import * as React from "react";
import { useNavigate } from "react-router-dom";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Card,
  FormControl,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useSelector } from "react-redux";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { RootState } from "store";
import { AxiosError } from "axios";
import SoftTypography from "components/SoftTypography";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

// ---------- TYPE ALIASES ----------

type DateInput = string | number | Date;

// ---------- INTERFACES ----------

interface Divisions {
  division_id: number;
  division_name: string;
}

interface AssetTypeOption {
  asset_type_id: number;
  asset_type_name: string;
}

interface SubscriptionTypeOption {
  subscription_type_id: number;
  subscription_type_name: string;
}

interface TableMetaData {
  currentTableData: Array<{ index: number }>;
  rowIndex: number;
  tableData: any[];
}

// ---------- EXTRACTED COMPONENTS ----------

interface AssetNameCellProps {
  assetName: string;
}

const AssetNameCell: React.FC<AssetNameCellProps> = ({ assetName }) => {
  const displayValue =
    assetName?.length > 20 ? `${assetName.substring(0, 20)}...` : assetName;
  return (
    <Tooltip title={assetName}>
      <span>{displayValue}</span>
    </Tooltip>
  );
};

interface StatusCellProps {
  value: string;
}

const StatusCell: React.FC<StatusCellProps> = ({ value }) => (
  <span
    className="active-btn"
    style={{
      borderColor: value ? "var(--primary-color, #2e7d32)" : "#fe346e",
    }}
  >
    {value ? "Active" : "Inactive"}
  </span>
);

interface ActionsCellProps {
  subscriptionId: number;
  onView: (subscriptionId: number) => void;
}

const ActionsCell: React.FC<ActionsCellProps> = ({
  subscriptionId,
  onView,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
    }}
  >
    <IconButton aria-label="view" onClick={() => onView(subscriptionId)}>
      <VisibilityIcon />
    </IconButton>
  </Box>
);

// Table options
const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

const CustomerSubscriptionList = () => {
  const navigate = useNavigate();
  const [divisionsFilter, setDivisionsFilter] = React.useState<number | null>(
    null
  );
  const [assetTypeFilter, setAssetTypeFilter] = React.useState<AssetTypeOption | null>(null);
  const [subscriptionTypeFilter, setSubscriptionTypeFilter] = React.useState<SubscriptionTypeOption | null>(null);
  const [divisions, setDivisions] = React.useState<Divisions[]>([]);
  const [assetTypes, setAssetTypes] = React.useState<AssetTypeOption[]>([]);
  const [subscriptionTypes, setSubscriptionTypes] = React.useState<SubscriptionTypeOption[]>([]);
  const [rows, setRows] = React.useState([]);
  const [error, setError] = React.useState<AxiosError | null>(null);
  const user = useSelector((state: RootState) => state.auth.user);

  const formatDate = (dateString: DateInput) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "2-digit",
    };
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", options).format(date);
  };

  const fetchDivisions = async () => {
    try {
      const response = await axios.get(`/divisions/customer/${user?.userId}`);
      setDivisions(response?.data?.divisions);
    } catch (error) {
      console.error("Error fetching statuses:", error);
    }
  };

  const fetchAssetTypes = React.useCallback(async () => {
    try {
      const res = await axios.get("/asset-types");
      const list = res.data?.list ?? [];
      setAssetTypes(
        list.map((a: AssetTypeOption) => ({
          asset_type_id: a.asset_type_id,
          asset_type_name: a.asset_type_name ?? "",
        }))
      );
    } catch (e) {
      console.error("Failed to fetch asset types", e);
    }
  }, []);

  const fetchSubscriptionTypes = React.useCallback(async () => {
    try {
      const res = await axios.get("/subscription-types");
      const list = res.data?.list ?? res.data ?? [];
      setSubscriptionTypes(
        list.map((s: SubscriptionTypeOption) => ({
          subscription_type_id: s.subscription_type_id,
          subscription_type_name: s.subscription_type_name ?? "",
        }))
      );
    } catch (e) {
      console.error("Failed to fetch subscription types", e);
    }
  }, []);

  React.useEffect(() => {
    fetchDivisions();
  }, []);

  React.useEffect(() => {
    fetchAssetTypes();
  }, [fetchAssetTypes]);

  React.useEffect(() => {
    fetchSubscriptionTypes();
  }, [fetchSubscriptionTypes]);

  const handleViewSubscription = (subscriptionId: number) => {
    navigate(`/view-subscription/${subscriptionId}`);
  };

  // Render functions for table columns
  const renderAssetNameCell = (value: string, tableMeta: TableMetaData) => {
    const { currentTableData, rowIndex } = tableMeta;
    const data = tableMeta?.tableData[currentTableData[rowIndex]?.index];
    const assetName = data?.asset_name;
    return <AssetNameCell assetName={assetName} />;
  };

  const renderStartDate = (value: DateInput) => {
    return formatDate(value);
  };

  const renderEndDate = (value: DateInput) => {
    return formatDate(value);
  };

  const renderStatusCell = (value: string) => {
    return <StatusCell value={value} />;
  };

  const renderActionsCell = (value: any, tableMeta: TableMetaData) => {
    const { currentTableData, rowIndex } = tableMeta;
    const data = tableMeta?.tableData[currentTableData[rowIndex]?.index];
    return (
      <ActionsCell
        subscriptionId={data?.subscription_id}
        onView={handleViewSubscription}
      />
    );
  };

  const columns: MUIDataTableColumn[] = [
    { name: "subscription_type_name", label: "Subscription Type" },
    ...(user?.type === "company" && user?.division_id === null
      ? [{ name: "division_name", label: "Division" }]
      : []),
    {
      name: "asset_name",
      label: "Brand Name",
      options: {
        filter: false,
        customBodyRender: renderAssetNameCell,
      },
    },

    { name: "asset_type_name", label: "Asset Type" },
    {
      name: "start_date",
      label: "Start Date",
      options: {
        filter: false,
        customBodyRender: renderStartDate,
      },
    },
    {
      name: "end_date",
      label: "End Date",
      options: {
        filter: false,
        customBodyRender: renderEndDate,
      },
    },
    {
      name: "is_active",
      label: "Status",
      options: {
        filter: false,
        customBodyRender: renderStatusCell,
      },
    },
    {
      name: "id",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: renderActionsCell,
      },
    },
  ];

  const fetchData = React.useMemo(
    () => async () => {
      try {
        const response = await axios.get(
          `/subscription/customer/${user?.userId}`
        );
        let subscriptions = response?.data?.list;
        if (divisionsFilter) {
          subscriptions = subscriptions.filter(
            (s: { division_id: number }) => s.division_id === divisionsFilter
          );
        }
        if (assetTypeFilter) {
          subscriptions = subscriptions.filter(
            (s: { asset_type_id: number }) => s.asset_type_id === assetTypeFilter.asset_type_id
          );
        }
        if (subscriptionTypeFilter) {
          subscriptions = subscriptions.filter(
            (s: { subscription_type_id: number }) =>
              s.subscription_type_id === subscriptionTypeFilter.subscription_type_id
          );
        }
        setRows(subscriptions);
      } catch (error) {
        if (error instanceof AxiosError && error) {
          setError(error);
        }
      }
    },
    [user, divisionsFilter, assetTypeFilter, subscriptionTypeFilter]
  );

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1.5, mb: 2 }}>
          <FormControl sx={{ width: 200, minWidth: 200 }}>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
              Asset Type
            </SoftTypography>
            <Autocomplete
              options={assetTypes}
              getOptionLabel={(o) => o.asset_type_name}
              value={assetTypeFilter}
              onChange={(_, v) => setAssetTypeFilter(v)}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" placeholder="All" />
              )}
            />
          </FormControl>
          <FormControl sx={{ width: 200, minWidth: 200 }}>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
              Subscription Type
            </SoftTypography>
            <Autocomplete
              options={subscriptionTypes}
              getOptionLabel={(o) => o.subscription_type_name}
              value={subscriptionTypeFilter}
              onChange={(_, v) => setSubscriptionTypeFilter(v)}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" placeholder="All" />
              )}
            />
          </FormControl>
          {user?.type === "company" && user?.division_id === null && (
            <FormControl sx={{ width: 200, minWidth: 200 }}>
              <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
                Divisions
              </SoftTypography>
              <Autocomplete
                options={divisions}
                getOptionLabel={(option) => option.division_name}
                value={divisions?.find((d) => d.division_id === divisionsFilter) ?? null}
                onChange={(event, value) => {
                  setDivisionsFilter(value?.division_id ?? null);
                }}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" size="small" placeholder="All" />
                )}
              />
            </FormControl>
          )}
        </Box>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-assets"
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={"Manage Subscriptions"}
              data={rows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
          {error && <div>Error: {error.message}</div>}
        </SoftBox>
      </Card>
    </DashboardLayout>
  );
};

export default CustomerSubscriptionList;
